import {Dispatch} from "redux";

import {IStore} from "../../app/reducers/hybrid_reducer";
import {apiLink} from "../../app/routes/api_link";
import {IRouteState} from "../../app/routes/data_fetcher/create_app_path_data_fetcher";
import {IServices} from "../../app/services/IServices";
import {appendQueryString} from "../../app/utils/append_query_string";
import {createRequestActionTypes} from "../../app/utils/request_response_utils/factories/create_request_action_types";
import {getRequest} from "../../app/utils/request_response_utils/request";
import {ISearchResultRouteParams} from "../../offer/list/actions/redirect_constraints";
import {parseOfferListPath} from "../../offer/list/url_utils/parse_offer_list_path";
import {locationParamFromValues} from "../../search/location_filter_utils";
import {fetchInitialSearchDataTypes} from "./fetch_initial_search_data";
import {fetchLocationScopedMakes} from "./fetch_location_scoped_makes";

export interface ICarsMakesSearch {
    make: string;
    count: number;
}
interface ICarsMakeSearchResponse {
    data: ICarsMakesSearch[];
}

export const fetchAllMakesForSearch = createRequestActionTypes({view: "Home", type: "GET", name: "CarsAllMakesForSearch"});

export const normalizeMakes = (json: unknown): ICarsMakesSearch[] => {
    if (Array.isArray(json)) {
        return json as ICarsMakesSearch[];
    }
    if (json && typeof json === "object" && Array.isArray((json as ICarsMakeSearchResponse).data)) {
        return (json as ICarsMakeSearchResponse).data;
    }
    return [];
};

/** Homepage search button only needs total offers — sum make counts instead of slow get-cars-search. */
const seedOfferCountFromMakes = (dispatch: Dispatch, makes: ICarsMakesSearch[]) => {
    const count = makes.reduce((sum, make) => sum + (Number(make.count) || 0), 0);
    dispatch({
        type: fetchInitialSearchDataTypes.success,
        result: {
            count,
            model: [],
            body_type: [],
            engine: [],
            year: []
        }
    });
};

const locationParamFromRoute = (route: IRouteState<ISearchResultRouteParams> | undefined, getState: (() => IStore) | undefined): string | undefined => {
    const fromStore = getState?.().carList.location.location?.slug;
    if (fromStore) {
        return fromStore;
    }
    if (!route?.params) {
        return undefined;
    }
    const parsed = parseOfferListPath(route.params);
    return locationParamFromValues(parsed.location || "", parsed.subLocation || "");
};

export const fetchMakesDataForSearch =
    (services: Partial<IServices>, route?: IRouteState<ISearchResultRouteParams>) =>
    (dispatch: Dispatch, getState?: () => IStore): Promise<ICarsMakeSearchResponse | boolean> => {
        dispatch({type: fetchAllMakesForSearch.start});
        const locationParam = locationParamFromRoute(route, getState);
        const url = locationParam
            ? appendQueryString(apiLink.searchMakes({})(null), {location: locationParam})
            : apiLink.searchMakes({})(null);

        return getRequest(services, url, "fetchMakesSearch")
            .then(async (json: unknown) => {
                let makes = normalizeMakes(json);
                if (locationParam) {
                    makes = await fetchLocationScopedMakes(services, makes, {location: locationParam});
                }
                dispatch({type: fetchAllMakesForSearch.success, result: {data: makes}});
                // Only seed global homepage count from unfiltered makes.
                if (!locationParam) {
                    seedOfferCountFromMakes(dispatch, makes);
                }
                return {data: makes};
            })
            .catch(async (_err) => {
                return false;
            });
    };
